import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface TaxonomySubcategory {
  id: number;
  name: string;
  categoryId: number;
  description: string | null;
  courseCount: number;
}
export interface TaxonomyCategory {
  id: number;
  name: string;
  description: string | null;
  courseCount: number;
  subcategories: TaxonomySubcategory[];
}

export type CourseStatus = 'draft' | 'pending' | 'published' | 'archived';

export interface GovernanceCourse {
  id: number;
  title: string;
  status: CourseStatus;
  publishedAt: string | null;
  updatedAt: string;
  createdAt: string;
  categoryName: string | null;
  creatorName: string | null;
  sectionCount: number;
  lessonCount: number;
}

export interface CourseStructure {
  course: { id: number; title: string; status: CourseStatus };
  sections: {
    id: number;
    title: string;
    sectionOrder: number;
    lessons: { id: number; title: string; lessonType: string; contentType: string | null; lessonOrder: number }[];
  }[];
}

export interface LifecycleHistoryEntry {
  id: number;
  fromStatus: CourseStatus | null;
  toStatus: CourseStatus;
  note: string | null;
  createdAt: string;
  actorUuid: string;
  actorName: string | null;
}

export type LifecycleAction = 'submit' | 'approve' | 'reject' | 'archive' | 'restore';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  countsByStatus?: Record<CourseStatus, number>;
}

type Result<T> = { success: boolean; data?: T; countsByStatus?: Record<CourseStatus, number>; error?: { message: string } | null; message?: string };

const BASE = '/super-admin/content-governance';
const ok = <T,>(data: T, extra: Partial<Result<T>> = {}): Result<T> => ({ success: true, data, error: null, ...extra });
const fail = <T,>(e: any): Result<T> => ({ success: false, error: { message: e?.message ?? 'Request failed' } });

// ===== TAXONOMY =====

export const getTaxonomy = async (): Promise<Result<TaxonomyCategory[]>> => {
  try {
    const res = await httpClient.get<ApiResponse<TaxonomyCategory[]>>(`${BASE}/taxonomy`);
    return ok(res.data || []);
  } catch (e) { return fail(e); }
};

export const createCategory = async (name: string, description?: string): Promise<Result<any>> => {
  try { const res = await httpClient.post<ApiResponse<any>>(`${BASE}/categories`, { name, description }); return ok(res.data, { message: res.message }); }
  catch (e) { return fail(e); }
};
export const updateCategory = async (id: number, name: string, description?: string): Promise<Result<any>> => {
  try { const res = await httpClient.put<ApiResponse<any>>(`${BASE}/categories/${id}`, { name, description }); return ok(res.data, { message: res.message }); }
  catch (e) { return fail(e); }
};
export const deleteCategory = async (id: number): Promise<Result<null>> => {
  try { const res = await httpClient.delete<ApiResponse<null>>(`${BASE}/categories/${id}`); return ok(null, { message: res.message }); }
  catch (e) { return fail(e); }
};
export const createSubcategory = async (categoryId: number, name: string, description?: string): Promise<Result<any>> => {
  try { const res = await httpClient.post<ApiResponse<any>>(`${BASE}/subcategories`, { categoryId, name, description }); return ok(res.data, { message: res.message }); }
  catch (e) { return fail(e); }
};
export const updateSubcategory = async (id: number, name: string, description?: string): Promise<Result<any>> => {
  try { const res = await httpClient.put<ApiResponse<any>>(`${BASE}/subcategories/${id}`, { name, description }); return ok(res.data, { message: res.message }); }
  catch (e) { return fail(e); }
};
export const deleteSubcategory = async (id: number): Promise<Result<null>> => {
  try { const res = await httpClient.delete<ApiResponse<null>>(`${BASE}/subcategories/${id}`); return ok(null, { message: res.message }); }
  catch (e) { return fail(e); }
};

// ===== COURSES / LIFECYCLE =====

export const getCourses = async (status?: CourseStatus | ''): Promise<Result<GovernanceCourse[]>> => {
  try {
    const q = status ? `?status=${status}` : '';
    const res = await httpClient.get<ApiResponse<GovernanceCourse[]>>(`${BASE}/courses${q}`);
    return ok(res.data || [], { countsByStatus: res.countsByStatus });
  } catch (e) { return fail(e); }
};

export const getCourseStructure = async (courseId: number): Promise<Result<CourseStructure>> => {
  try { const res = await httpClient.get<ApiResponse<CourseStructure>>(`${BASE}/courses/${courseId}/structure`); return ok(res.data); }
  catch (e) { return fail(e); }
};

export const getCourseHistory = async (courseId: number): Promise<Result<LifecycleHistoryEntry[]>> => {
  try { const res = await httpClient.get<ApiResponse<LifecycleHistoryEntry[]>>(`${BASE}/courses/${courseId}/history`); return ok(res.data || []); }
  catch (e) { return fail(e); }
};

export const transitionLifecycle = async (courseId: number, action: LifecycleAction, note?: string): Promise<Result<{ status: CourseStatus }>> => {
  try {
    const res = await httpClient.patch<ApiResponse<{ status: CourseStatus }>>(`${BASE}/courses/${courseId}/lifecycle`, { action, note });
    return ok(res.data, { message: res.message });
  } catch (e) { return fail(e); }
};
